import React, { useState, useEffect, DragEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { functions, db, storage, auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { validatePairedUpload, formatValidationMessage, ValidationResult } from '../utils/pairedUploadValidation';
import type { UploadMode } from '../types/onboarding';
import TemplateSelector from './TemplateSelector';
import { AggregatorLabelsGroup } from './AggregatorLabelsGroup';

// CVE MITIGATION: ZIP safety limits to prevent zip bomb attacks
const MAX_ZIP_ENTRIES = 1000; // Maximum files allowed in a ZIP archive
const MAX_EXCEL_PREVIEW_SIZE_MB = 25; // Maximum file size for Excel preview (MB)

interface EntityMapping {
  code: string;
  officeId: string;
  officeName: string;
}

interface FileTemplate {
  id: string;
  templateName: string;
}

interface DetectedSheet {
  name: string;
  rowCount: number;
}

interface Office {
  id: string;
  name: string;
}

interface UploaderProps {
  companyId?: string; // Optional for onboarding context
  isOnboarding?: boolean; // New flag for onboarding wizard mode
  preselectedTemplateId?: string; // Template preselection for onboarding
  preselectedOfficeId?: string; // Office preselection for onboarding (not used in onboarding, but for consistency)
  onboardingEntityMappings?: Array<{ code: string; officeName: string }>; // Entity mappings from wizard (before company exists)
  onUploadSuccess?: (result?: {
    sourceDocId?: string;
    filesQueued?: number;
    filesSkipped?: number;
  }) => void;
  onFileSelected?: (data: { zipFile?: File | null; rctiFile?: File | null; csvFile?: File | null; templateId?: string; uploadMode?: UploadMode }) => void; // For onboarding wizard to track file selection
}

const OnboardingDataUploader: React.FC<UploaderProps> = ({
  companyId,
  isOnboarding = false,
  preselectedTemplateId,
  preselectedOfficeId,
  onboardingEntityMappings,
  onUploadSuccess,
  onFileSelected
}) => {
  const [fileTemplates, setFileTemplates] = useState<FileTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(preselectedTemplateId || '');
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0); // Used in legacy ZIP upload

  // New state for paired upload mode
  // Default to 'legacy' for ZIP uploads
  const [uploadMode, setUploadMode] = useState<UploadMode>('legacy');
  const [rctiFile, setRctiFile] = useState<File | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  // State for ZIP with pairs preview
  const [zipPreview, setZipPreview] = useState<{ rctiCount: number; csvCount: number } | null>(null);

  // State for multi-entity upload mode
  const [multiEntityPreview, setMultiEntityPreview] = useState<{
    entities: string[];
    excelCount: number;
    pdfCount: number;
  } | null>(null);
  const [entityMappings, setEntityMappings] = useState<EntityMapping[]>([]);

  // State for multi-sheet detection
  const [isMultiSheet, setIsMultiSheet] = useState(false);
  const [detectedSheets, setDetectedSheets] = useState<DetectedSheet[]>([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Auto-select Entors mode when Entors template is selected
  useEffect(() => {
    const template = fileTemplates.find(t => t.id === selectedTemplateId);
    const templateName = (template?.templateName || '').toLowerCase();
    if (templateName.includes('entors') && uploadMode !== 'entorsPaired') {
      setUploadMode('entorsPaired');
      // Notify parent of mode change
      if (onFileSelected) {
        onFileSelected({ uploadMode: 'entorsPaired', zipFile, templateId: selectedTemplateId });
      }
    }
  }, [selectedTemplateId, fileTemplates]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Use getTemplates cloud function (bypasses Firestore rules, requires isFounder).
        // On a real company page, scope the dropdown to ONLY the templates this
        // tenant uses (mode-aware backend resolution). During onboarding the
        // company doesn't exist yet, so request the full catalogue minus demo
        // templates. Both paths return alphabetically sorted results.
        const getTemplatesFn = httpsCallable(functions, 'getTemplates');
        const payload = !isOnboarding && companyId ? { companyId, excludeDemo: true } : { excludeDemo: true };
        const result = await getTemplatesFn(payload) as any;

        if (result.data.success) {
          const fetchedTemplates = result.data.templates.map((t: any) => ({
            id: t.id,
            templateName: t.name || t.templateName,
          } as FileTemplate));
          setFileTemplates(fetchedTemplates);
          if (fetchedTemplates.length > 0 && !preselectedTemplateId) {
            setSelectedTemplateId(fetchedTemplates[0].id);
          }
        }
      } catch (_e) {
        console.error("[OnboardingDataUploader] Failed to fetch templates:", _e);
        setError("Could not load file templates.");
      }
    };
    fetchTemplates();
  }, [preselectedTemplateId, companyId, isOnboarding]);

  // Load entity mappings when multi-entity mode is selected
  useEffect(() => {
    const loadEntityMappings = async () => {
      if (uploadMode !== 'multiEntity') return;

      // During onboarding, use the mappings passed from wizard (company doesn't exist yet)
      if (isOnboarding && onboardingEntityMappings && onboardingEntityMappings.length > 0) {
        const mappings = onboardingEntityMappings.map(m => ({
          code: m.code,
          officeId: '', // Office ID not known yet during onboarding
          officeName: m.officeName
        }));
        setEntityMappings(mappings);
        return;
      }

      // For existing companies, load from Firestore
      if (!companyId) return;

      try {
        const mappingsQuery = query(collection(db, `companies/${companyId}/entity_mappings`));
        const snapshot = await getDocs(mappingsQuery);
        const mappings = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            code: doc.id,
            officeId: data.officeId || '',
            officeName: data.officeName || doc.id
          };
        });
        setEntityMappings(mappings);
      } catch (err) {
        console.error('Failed to load entity mappings:', err);
      }
    };

    loadEntityMappings();
  }, [companyId, uploadMode, isOnboarding, onboardingEntityMappings]);

  useEffect(() => {
    // Skip office fetching in onboarding mode (no companyId yet)
    if (isOnboarding) {
      return;
    }

    const fetchOffices = async () => {
      if (!companyId) return;

      try {
        const officesQuery = query(
          collection(db, `companies/${companyId}/offices`)
        );
        const querySnapshot = await getDocs(officesQuery);
        const fetchedOffices = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        } as Office));
        setOffices(fetchedOffices);
        if (fetchedOffices.length > 0) {
          setSelectedOfficeId(fetchedOffices[0].id);
        }
      } catch (_e) {
  // console.error("Failed to fetch offices:", _e);
        setError("Could not load offices.");
      }
    };
    fetchOffices();
  }, [companyId, isOnboarding]);

  // Helper function for file to base64 conversion
  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]); // Remove data:type prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    // In onboarding mode, we don't handle upload directly - the wizard does
    if (isOnboarding) {
      setError("In onboarding mode, the wizard handles file upload.");
      return;
    }

    // Validation based on upload mode
    if (uploadMode === 'paired') {
      // Validate paired files
      if (!validationResult?.isValid) {
        setError('Please upload valid RCTI and CSV files');
        return;
      }
      if (!selectedTemplateId) {
        setError("Please select a file template.");
        return;
      }
      if (!selectedOfficeId) {
        setError("Please select an office.");
        return;
      }

      setLoading('Preparing paired upload...');
      setError(null);

      try {
        // Convert files to base64
        const rctiBase64 = await fileToBase64(rctiFile!);
        const csvBase64 = await fileToBase64(csvFile!);

        setLoading('Uploading files...');

        const uploadSupplementalData = httpsCallable(functions, 'uploadSupplementalData');
        const result = await uploadSupplementalData({
          companyId,
          officeId: selectedOfficeId,
          templateId: selectedTemplateId,
          uploadMode: 'paired',
          pairedFiles: {
            rctiFile: {
              name: rctiFile!.name,
              size: rctiFile!.size,
              paymentNumber: validationResult.paymentNumber!,
              paymentDate: validationResult.paymentDate!,
              runNumber: validationResult.runNumber!,
              month: validationResult.month!,
              year: validationResult.year!,
              base64Content: rctiBase64
            },
            csvFile: {
              name: csvFile!.name,
              size: csvFile!.size,
              paymentNumber: validationResult.paymentNumber!,
              base64Content: csvBase64
            }
          }
        });

        setLoading(null);
        setRctiFile(null);
        setCsvFile(null);
        setValidationResult(null);

        if (onUploadSuccess) {
          onUploadSuccess(result.data as any);
        }
      } catch (err: any) {
        setError(`Upload failed: ${err.message}`);
        setLoading(null);
      }

    } else if (uploadMode === 'zipWithPairs') {
      // ZIP with RCTI+CSV Pairs upload processing
      if (!zipFile) {
        setError("Please select a ZIP file to upload.");
        return;
      }
      if (!selectedTemplateId) {
        setError("Please select a file template.");
        return;
      }
      if (!selectedOfficeId) {
        setError("Please select an office.");
        return;
      }
      if (zipFile.size > 50 * 1024 * 1024) {
        setError('File size cannot exceed 50MB for batch uploads.');
        return;
      }

      setLoading('Preparing batch upload...');
      setError(null);
      setUploadProgress(0);

      try {
        // CRITICAL: Verify authentication and refresh token before upload
        // Fixes regression where expired tokens cause 401 errors
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError('Authentication required. Please refresh the page and log in again.');
          setLoading(null);
          return;
        }

        // Force token refresh to ensure it's valid and not expired
        try {
          const tokenResult = await currentUser.getIdTokenResult(true); // force refresh

          // Verify user has required role for supplemental uploads
          const hasFounderRole = tokenResult.claims.isFounder === true ||
                                tokenResult.claims.role === 'founder';
          if (!hasFounderRole) {
            setError('Insufficient permissions. Only founders can upload supplemental data.');
            setLoading(null);
            return;
          }

          console.log('[Upload] Auth token refreshed successfully', {
            uid: currentUser.uid,
            role: tokenResult.claims.role,
            expirationTime: tokenResult.expirationTime
          });
        } catch (tokenError) {
          console.error('[Upload] Token refresh failed:', tokenError);
          setError('Authentication token refresh failed. Please refresh the page and try again.');
          setLoading(null);
          return;
        }

        const taskId = uuidv4();
        const storagePath = `companies/${companyId}/supplemental/${taskId}/${zipFile.name}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, zipFile);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              setLoading(`Uploading... ${Math.round(progress)}%`);
            },
            (error) => reject(error),
            () => resolve()
          );
        });

        const fullPath = uploadTask.snapshot.ref.fullPath;

        setLoading('Processing ZIP with RCTI+CSV pairs...');

        const uploadSupplementalData = httpsCallable(functions, 'uploadSupplementalData');
        const result = await uploadSupplementalData({
          companyId,
          officeId: selectedOfficeId,
          fileMeta: {
            fileName: zipFile.name,
            mimeType: zipFile.type || 'application/zip',
            storagePath: fullPath,
          },
          templateId: selectedTemplateId,
          uploadMode: 'zipWithPairs'
        });

        setLoading(null);
        setZipFile(null);

        // Display results with RCTI pair count
        if (onUploadSuccess) {
          const resultData = result.data as any;
          if (resultData.rctiPairsFound > 0) {
            // Include success message in the result
            resultData.successMessage = `Successfully processed ${resultData.rctiPairsFound} RCTI+CSV pairs from ${resultData.filesQueued || 'multiple'} total files.`;
          }
          onUploadSuccess(resultData);
        }
      } catch (err: any) {
        setError(`Upload failed: ${err.message}`);
        setLoading(null);
      }
    } else if (uploadMode === 'multiEntity') {
      // Multi-Entity ZIP upload processing
      if (!zipFile) {
        setError("Please select a ZIP file to upload.");
        return;
      }
      if (!selectedTemplateId) {
        setError("Please select a file template.");
        return;
      }
      if (entityMappings.length === 0) {
        setError("No entity mappings configured. Please set up entity mappings first.");
        return;
      }
      if (zipFile.size > 100 * 1024 * 1024) {
        setError('File size cannot exceed 100MB for multi-entity uploads.');
        return;
      }

      // Check for unmapped entities
      if (multiEntityPreview) {
        const unmappedEntities = multiEntityPreview.entities.filter(
          code => !entityMappings.find(m => m.code === code)
        );
        if (unmappedEntities.length > 0) {
          setError(`Unmapped entities found: ${unmappedEntities.join(', ')}. Configure entity mappings before uploading.`);
          return;
        }
      }

      setLoading('Preparing multi-entity upload...');
      setError(null);
      setUploadProgress(0);

      try {
        // CRITICAL: Verify authentication and refresh token before upload
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError('Authentication required. Please refresh the page and log in again.');
          setLoading(null);
          return;
        }

        // Force token refresh
        try {
          const tokenResult = await currentUser.getIdTokenResult(true);
          const hasFounderRole = tokenResult.claims.isFounder === true ||
                                tokenResult.claims.role === 'founder';
          if (!hasFounderRole) {
            setError('Insufficient permissions. Only founders can upload multi-entity data.');
            setLoading(null);
            return;
          }
        } catch (tokenError) {
          console.error('[MultiEntity] Token refresh failed:', tokenError);
          setError('Authentication token refresh failed. Please refresh the page and try again.');
          setLoading(null);
          return;
        }

        const taskId = uuidv4();
        const storagePath = `companies/${companyId}/supplemental/${taskId}/${zipFile.name}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, zipFile);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              setLoading(`Uploading... ${Math.round(progress)}%`);
            },
            (error) => reject(error),
            () => resolve()
          );
        });

        const fullPath = uploadTask.snapshot.ref.fullPath;

        setLoading('Processing multi-entity ZIP...');

        // Call the admin multi-entity upload function
        const processAdminMultiEntityUpload = httpsCallable(functions, 'processAdminMultiEntityUpload');
        const result = await processAdminMultiEntityUpload({
          companyId,
          fileMeta: {
            fileName: zipFile.name,
            mimeType: zipFile.type || 'application/zip',
            storagePath: fullPath,
          },
          templateId: selectedTemplateId
        });

        setLoading(null);
        setZipFile(null);
        setMultiEntityPreview(null);

        if (onUploadSuccess) {
          const resultData = result.data as any;
          resultData.successMessage = `Successfully processed ${resultData.entitiesProcessed?.length || 0} entities with ${resultData.filesQueued || 0} files queued.`;
          onUploadSuccess(resultData);
        }
      } catch (err: any) {
        setError(`Multi-entity upload failed: ${err.message}`);
        setLoading(null);
      }
    } else {
      // Legacy ZIP upload processing
      if (!zipFile) {
        setError("Please select a ZIP file to upload.");
        return;
      }
      if (!selectedTemplateId) {
        setError("Please select a file template.");
        return;
      }
      if (!selectedOfficeId) {
        setError("Please select an office.");
        return;
      }
      if (zipFile.size > 50 * 1024 * 1024) {
        setError('File size cannot exceed 50MB.');
        return;
      }

      setLoading('Preparing upload...');
      setError(null);
      setUploadProgress(0);

      try {
        const taskId = uuidv4();
        const storagePath = `companies/${companyId}/supplemental/${taskId}/${zipFile.name}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, zipFile);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              setLoading(`Uploading... ${Math.round(progress)}%`);
            },
            (error) => reject(error),
            () => resolve()
          );
        });

        const fullPath = uploadTask.snapshot.ref.fullPath;

        setLoading('Processing ZIP file...');

        const uploadSupplementalData = httpsCallable(functions, 'uploadSupplementalData');
        const result = await uploadSupplementalData({
          companyId,
          officeId: selectedOfficeId,
          fileMeta: {
            fileName: zipFile.name,
            mimeType: zipFile.type || 'application/zip',
            storagePath: fullPath,
          },
          templateId: selectedTemplateId,
          uploadMode: 'legacy'
        });

        setLoading(null);
        setZipFile(null);

        // Pass result data to parent (includes sourceDocId for monitoring)
        if (onUploadSuccess) {
          onUploadSuccess(result.data as any);
        }
      } catch (err: any) {
    // console.error("Upload failed:", err);
        setError(`Upload failed: ${err.message}`);
        setLoading(null);
      }
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0 && (files[0].type === "application/zip" || files[0].name.endsWith('.zip'))) {
      const selectedFile = files[0];
      setZipFile(selectedFile);
      setError(null);

      // Preview ZIP contents for zipWithPairs mode
      if (uploadMode === 'zipWithPairs') {
        previewZipContents(selectedFile);
      }
    } else {
      setError("Invalid file type. Please upload a .zip file.");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setZipFile(selectedFile);
      setError(null);

      // Preview ZIP contents based on mode
      if (uploadMode === 'zipWithPairs') {
        previewZipContents(selectedFile);
      } else if (uploadMode === 'multiEntity') {
        previewMultiEntityZip(selectedFile);
      }

      // Detect multi-sheet Excel files for legacy mode
      if (uploadMode === 'legacy') {
        await detectSheets(selectedFile);
      }

      // Notify parent component (for onboarding wizard)
      if (onFileSelected) {
        onFileSelected({
          zipFile: selectedFile,
          templateId: selectedTemplateId,
          uploadMode: uploadMode
        });
      }
    }
  };

  // Extract entity code from filename (e.g., "ENTO100 AGMT RUN 1 Nov 25.xlsx" -> "ENTO100")
  const extractEntityCode = (filename: string): string | null => {
    const match = filename.match(/^([A-Z]+\d+)\s/);
    return match ? match[1] : null;
  };

  // Preview ZIP contents for multi-entity mode
  const previewMultiEntityZip = async (file: File) => {
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);

      // CVE MITIGATION: Check entry count to prevent zip bomb attacks
      const entryCount = Object.keys(contents.files).length;
      if (entryCount > MAX_ZIP_ENTRIES) {
        setError(`ZIP contains too many files (${entryCount.toLocaleString()}). Maximum: ${MAX_ZIP_ENTRIES.toLocaleString()}`);
        return;
      }

      const entities = new Set<string>();
      let excelCount = 0;
      let pdfCount = 0;

      Object.keys(contents.files).forEach(filename => {
        const lowerName = filename.toLowerCase();

        // Skip directories and macOS artifacts
        if (contents.files[filename].dir || lowerName.includes('__macosx') || filename.startsWith('.')) {
          return;
        }

        // Extract entity code
        const entityCode = extractEntityCode(filename);
        if (entityCode) {
          entities.add(entityCode);
        }

        // Count file types
        if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || lowerName.endsWith('.csv')) {
          excelCount++;
        } else if (lowerName.endsWith('.pdf')) {
          pdfCount++;
        }
      });

      setMultiEntityPreview({
        entities: Array.from(entities).sort(),
        excelCount,
        pdfCount
      });
    } catch (err) {
      console.error('Error previewing multi-entity ZIP:', err);
      setError('Failed to preview ZIP contents. Please ensure the file is a valid ZIP archive.');
    }
  };

  // Preview ZIP contents for zipWithPairs mode
  const previewZipContents = async (file: File) => {
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);

      // CVE MITIGATION: Check entry count to prevent zip bomb attacks
      const entryCount = Object.keys(contents.files).length;
      if (entryCount > MAX_ZIP_ENTRIES) {
        setError(`ZIP contains too many files (${entryCount.toLocaleString()}). Maximum: ${MAX_ZIP_ENTRIES.toLocaleString()}`);
        return;
      }

      let rctiCount = 0;
      let csvCount = 0;

      Object.keys(contents.files).forEach(filename => {
        const lowerName = filename.toLowerCase();

        // Skip directories and macOS artifacts
        if (contents.files[filename].dir || lowerName.includes('__macosx')) {
          return;
        }

        // Count RCTI PDFs (filename pattern: RCTI-*.pdf)
        if (lowerName.endsWith('.pdf') && lowerName.includes('rcti')) {
          rctiCount++;
        }

        // Count CSV files
        if (lowerName.endsWith('.csv')) {
          csvCount++;
        }
      });

      setZipPreview({ rctiCount, csvCount });
    } catch (err) {
      console.error('Error previewing ZIP contents:', err);
      setError('Failed to preview ZIP contents. Please ensure the file is a valid ZIP archive.');
    }
  };

  // Detect multi-sheet Excel files
  const detectSheets = async (file: File) => {
    try {
      // Only process Excel files
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setIsMultiSheet(false);
        setDetectedSheets([]);
        setShowTemplateSelector(false);
        return;
      }

      // CVE MITIGATION: Check file size before reading into memory
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_EXCEL_PREVIEW_SIZE_MB) {
        console.warn(`[DETECT_SHEETS] File too large for preview: ${fileSizeMB.toFixed(1)}MB`);
        setError(`Excel file too large for preview (${fileSizeMB.toFixed(1)}MB). Maximum: ${MAX_EXCEL_PREVIEW_SIZE_MB}MB`);
        setIsMultiSheet(false);
        setDetectedSheets([]);
        setShowTemplateSelector(false);
        return;
      }

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      // Check if multi-sheet
      if (workbook.SheetNames.length > 1) {
        const sheets: DetectedSheet[] = workbook.SheetNames.map(name => {
          const worksheet = workbook.Sheets[name];
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
          const rowCount = range.e.r - range.s.r + 1;

          return {
            name,
            rowCount
          };
        });

        setIsMultiSheet(true);
        setDetectedSheets(sheets);
        setShowTemplateSelector(true);
        setPendingFile(file);
      } else {
        // Single sheet
        setIsMultiSheet(false);
        setDetectedSheets([]);
        setShowTemplateSelector(false);
      }
    } catch (err) {
      console.error('Error detecting sheets:', err);
      setError('Failed to analyze Excel file structure. Please ensure the file is valid.');
    }
  };

  const inputClasses = "w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500";
  const buttonClasses = "w-full py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all";

  return (
    <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
      <h3 className="text-xl font-semibold mb-1">Upload Data File</h3>
      <p className="text-slate-400 text-sm mb-6">Upload commission data files.</p>

      {error && (
        <div className="p-3 my-4 bg-red-500/20 border border-red-500/30 rounded-md text-center">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Upload Mode Selector */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Select Upload Method</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              uploadMode === 'legacy'
                ? 'border-fuchsia-500 bg-fuchsia-500/10'
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onClick={() => {
              setUploadMode('legacy');
              setMultiEntityPreview(null);
              if (onFileSelected) {
                onFileSelected({ uploadMode: 'legacy', zipFile, templateId: selectedTemplateId });
              }
            }}
          >
            <div className="text-2xl mb-2">📦</div>
            <div className="font-semibold text-white mb-1 text-sm">ZIP Archive</div>
            <div className="text-xs text-slate-400">
              Single office bulk upload.
            </div>
            <AggregatorLabelsGroup uploadMode="legacy" />
            <div className="mt-3 pt-2 border-t border-slate-700">
              <span className="text-xs text-green-400 font-medium">BATCH HISTORICAL</span>
            </div>
          </div>

          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              uploadMode === 'zipWithPairs'
                ? 'border-fuchsia-500 bg-fuchsia-500/10'
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onClick={() => {
              setUploadMode('zipWithPairs');
              setMultiEntityPreview(null);
              if (onFileSelected) {
                onFileSelected({ uploadMode: 'zipWithPairs', zipFile, templateId: selectedTemplateId });
              }
            }}
          >
            <div className="text-2xl mb-2">📄📊</div>
            <div className="font-semibold text-white mb-1 text-sm">RCTI+CSV Pairs</div>
            <div className="text-xs text-slate-400">
              AFG single office (CSV only).
            </div>
            <AggregatorLabelsGroup uploadMode="zipWithPairs" />
            <div className="mt-3 pt-2 border-t border-slate-700">
              <div className="text-xs text-slate-500">Payment matching</div>
            </div>
          </div>

          {/* Entors ZIP */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              uploadMode === 'entorsPaired'
                ? 'border-fuchsia-500 bg-fuchsia-500/10'
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onClick={() => {
              setUploadMode('entorsPaired');
              setMultiEntityPreview(null);
              if (onFileSelected) {
                onFileSelected({ uploadMode: 'entorsPaired', zipFile, templateId: selectedTemplateId });
              }
            }}
          >
            <div className="text-2xl mb-2">🔗</div>
            <div className="font-semibold text-white mb-1 text-sm">Entors ZIP</div>
            <div className="text-xs text-slate-400">
              RID-based PDF+Excel pairing.
            </div>
            <AggregatorLabelsGroup uploadMode="entorsPaired" />
            {uploadMode === 'entorsPaired' && (
              <div className="mt-2 text-xs font-semibold text-fuchsia-400">RID MATCHING</div>
            )}
          </div>

          {/* Multi-Entity ZIP - NEW */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              uploadMode === 'multiEntity'
                ? 'border-cyan-500 bg-cyan-500/10'
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onClick={() => {
              setUploadMode('multiEntity' as UploadMode);
              setZipPreview(null);
              if (onFileSelected) {
                onFileSelected({ uploadMode: 'multiEntity' as UploadMode, zipFile, templateId: selectedTemplateId });
              }
            }}
          >
            <div className="text-2xl mb-2">🏢🏢🏢</div>
            <div className="font-semibold text-white mb-1 text-sm">Multi-Entity ZIP</div>
            <div className="text-xs text-slate-400">
              Multi-office (ENTO100, ENTO101...).
            </div>
            <AggregatorLabelsGroup uploadMode="multiEntity" />
            <div className="mt-3 pt-2 border-t border-slate-700">
              <span className="text-xs text-cyan-400 font-medium">LOAN MARKET MULTI-OFFICE</span>
            </div>
          </div>
        </div>
      </div>

      {/* File Template Selector */}
      <div className="mb-6">
        <label htmlFor="template" className="block text-sm font-medium text-slate-300 mb-1">File Template</label>
        <select
          id="template"
          value={selectedTemplateId}
          onChange={e => {
            const newTemplateId = e.target.value;
            setSelectedTemplateId(newTemplateId);
            // Notify parent component (for onboarding wizard)
            if (onFileSelected) {
              onFileSelected({
                templateId: newTemplateId,
                zipFile: zipFile,
                rctiFile: rctiFile,
                csvFile: csvFile,
                uploadMode: uploadMode
              });
            }
          }}
          className={inputClasses}
        >
          {fileTemplates.length === 0 && <option>Loading templates...</option>}
          {fileTemplates.map(template => (
            <option key={template.id} value={template.id}>{template.templateName}</option>
          ))}
        </select>
      </div>

      <div className="space-y-6">
        {/* Hide office selector in onboarding mode */}
        {!isOnboarding && (
          <div>
            <label htmlFor="office" className="block text-sm font-medium text-slate-300 mb-1">
              Office
            </label>
            <select
              id="office"
              value={selectedOfficeId}
              onChange={e => setSelectedOfficeId(e.target.value)}
              className={inputClasses}
              disabled={offices.length === 0}
            >
              {offices.length === 0 && <option>Loading offices...</option>}
              {offices.map(office => (
                <option key={office.id} value={office.id}>{office.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Show multi-sheet template selector if detected */}
        {showTemplateSelector && (
          <TemplateSelector
            companyId={companyId}
            isMultiSheet={isMultiSheet}
            detectedSheets={detectedSheets}
            onTemplateSelect={(templateId) => setSelectedTemplateId(templateId)}
            onCreateNew={() => {
              // Navigate to template creation page (to be implemented)
              alert('Template creation from uploader will be implemented. For now, please create templates from the Templates page.');
            }}
            selectedTemplateId={selectedTemplateId}
          />
        )}

        {/* Conditional rendering based on upload mode */}
        {uploadMode === 'entorsPaired' ? (
          <div className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-300">Entors RID-Based Pairing</h4>
              <ul className="space-y-1 text-xs text-slate-400">
                <li>• PDF and Excel files matched by RID (Report ID) from contents</li>
                <li>• RID extracted automatically - no filename conventions needed</li>
                <li>• Entors always uses RUN 1 (single monthly payment)</li>
              </ul>
            </div>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex justify-center px-6 pt-8 pb-8 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-fuchsia-500' : ''}`}
            >
              <div className="space-y-1 text-center">
                <div className="text-6xl mb-4">📦</div>
                <div className="flex text-lg text-slate-300 font-medium justify-center">
                  <label htmlFor="zipfile-entors" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                    <span>{zipFile ? `Selected: ${zipFile.name}` : "Click to upload Entors ZIP"}</span>
                    <input id="zipfile-entors" type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={handleFileSelect} className="sr-only" />
                  </label>
                </div>
                <p className="text-sm text-slate-500">ZIP containing PDF (RCTI) and Excel files</p>
                {zipFile && (
                  <p className="text-sm text-slate-400 mt-2">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                )}
              </div>
            </div>
          </div>
        ) : uploadMode === 'zipWithPairs' ? (
          <div className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-300">📋 Multi-Year Upload Guidelines:</h4>
              <ul className="space-y-1 text-sm text-slate-300">
                <li>✓ Organize by year: 2023.zip, 2024.zip, 2025.zip</li>
                <li>✓ Each ZIP can contain 12-36 months of data</li>
                <li>✓ Max file size: 50MB per ZIP (increased for batch uploads)</li>
                <li>✓ Include matching RCTI PDFs and CSV files</li>
                <li>✓ Payment numbers must match between RCTI and CSV</li>
              </ul>
            </div>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex justify-center px-6 pt-8 pb-8 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-fuchsia-500' : ''}`}
            >
              <div className="space-y-1 text-center">
                <div className="text-6xl mb-4">📦</div>
                <div className="flex text-lg text-slate-300 font-medium justify-center">
                  <label htmlFor="zipfile" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                    <span>{zipFile ? `Selected: ${zipFile.name}` : "Click to upload ZIP with RCTI+CSV pairs"}</span>
                    <input id="zipfile" type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={handleFileSelect} className="sr-only" />
                  </label>
                </div>
                <p className="text-sm text-slate-500">or drag and drop (max 50MB)</p>
                {zipFile && (
                  <p className="text-sm text-slate-400 mt-2">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                )}
              </div>
            </div>

            {/* ZIP Preview Results */}
            {zipPreview && (
              <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-green-300">✅ ZIP Contents Preview:</h4>
                <div className="grid grid-cols-2 gap-4 text-sm text-slate-300">
                  <div>
                    <span className="font-medium">📄 RCTI PDFs:</span>
                    <span className="ml-2">{zipPreview.rctiCount}</span>
                  </div>
                  <div>
                    <span className="font-medium">📊 CSV Files:</span>
                    <span className="ml-2">{zipPreview.csvCount}</span>
                  </div>
                </div>
                {zipPreview.rctiCount > 0 && zipPreview.csvCount > 0 && (
                  <p className="text-xs text-green-400 mt-2">
                    Backend will automatically pair matching RCTI PDFs with CSV files by payment number.
                  </p>
                )}
                {zipPreview.rctiCount === 0 && (
                  <p className="text-xs text-yellow-400 mt-2">
                    ⚠️ No RCTI files detected. Consider using "ZIP Archive (Legacy)" mode instead.
                  </p>
                )}
              </div>
            )}
          </div>
        ) : uploadMode === 'multiEntity' ? (
          <div className="space-y-4">
            <div className="bg-cyan-900/20 border border-cyan-500/30 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-cyan-300">🏢 Multi-Entity Upload (Loan Market)</h4>
              <ul className="space-y-1 text-xs text-slate-400">
                <li>• Files auto-routed by entity code (ENTO100, ENTO101, ENTO104...)</li>
                <li>• Each entity mapped to its office via entity_mappings</li>
                <li>• Include both Excel/CSV data files and PDF RCTIs</li>
                <li>• Filename format: <code className="text-cyan-300">ENTO100 AGMT RUN 1 Nov 25.xlsx</code></li>
              </ul>
            </div>

            {/* Entity Mappings Status */}
            {entityMappings.length > 0 ? (
              <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-green-300">✅ Entity Mappings Configured:</h4>
                <div className="grid grid-cols-3 gap-2">
                  {entityMappings.map(m => (
                    <div key={m.code} className="text-xs bg-slate-800 px-2 py-1 rounded">
                      <span className="font-mono text-cyan-300">{m.code}</span>
                      <span className="text-slate-400"> → {m.officeName}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-yellow-300">⚠️ No Entity Mappings Found</h4>
                <p className="text-xs text-slate-400">
                  Entity mappings must be configured before using multi-entity upload.
                  Go to Settings → Entity Mappings to configure.
                </p>
              </div>
            )}

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex justify-center px-6 pt-8 pb-8 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-cyan-500' : ''}`}
            >
              <div className="space-y-1 text-center">
                <div className="text-6xl mb-4">🏢📦</div>
                <div className="flex text-lg text-slate-300 font-medium justify-center">
                  <label htmlFor="zipfile-multiEntity" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-cyan-400 hover:text-cyan-300">
                    <span>{zipFile ? `Selected: ${zipFile.name}` : "Click to upload Multi-Entity ZIP"}</span>
                    <input id="zipfile-multiEntity" type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={handleFileSelect} className="sr-only" />
                  </label>
                </div>
                <p className="text-sm text-slate-500">ZIP containing files for multiple entities (ENTO100, ENTO101...)</p>
                {zipFile && (
                  <p className="text-sm text-slate-400 mt-2">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                )}
              </div>
            </div>

            {/* Multi-Entity Preview Results */}
            {multiEntityPreview && (
              <div className="bg-cyan-900/20 border border-cyan-500/30 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-cyan-300">📊 ZIP Contents Preview:</h4>
                <div className="grid grid-cols-3 gap-4 text-sm text-slate-300 mb-3">
                  <div>
                    <span className="font-medium">🏢 Entities:</span>
                    <span className="ml-2">{multiEntityPreview.entities.length}</span>
                  </div>
                  <div>
                    <span className="font-medium">📊 Data Files:</span>
                    <span className="ml-2">{multiEntityPreview.excelCount}</span>
                  </div>
                  <div>
                    <span className="font-medium">📄 PDFs:</span>
                    <span className="ml-2">{multiEntityPreview.pdfCount}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {multiEntityPreview.entities.map(code => {
                    const mapped = entityMappings.find(m => m.code === code);
                    return (
                      <span
                        key={code}
                        className={`px-2 py-1 rounded text-xs font-mono ${
                          mapped
                            ? 'bg-green-900/50 text-green-300 border border-green-600'
                            : 'bg-red-900/50 text-red-300 border border-red-600'
                        }`}
                      >
                        {code} {mapped ? `→ ${mapped.officeName}` : '(unmapped!)'}
                      </span>
                    );
                  })}
                </div>
                {multiEntityPreview.entities.some(code => !entityMappings.find(m => m.code === code)) && (
                  <p className="text-xs text-yellow-400 mt-2">
                    ⚠️ Some entities are not mapped. Configure entity mappings before uploading.
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex justify-center px-6 pt-5 pb-6 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-fuchsia-500' : ''}`}
            >
              <div className="space-y-1 text-center">
                <div className="flex text-sm text-slate-400">
                  <label htmlFor="zipfile" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                    <span>{zipFile ? `Selected: ${zipFile.name}` : "Upload a file"}</span>
                    <input id="zipfile" type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={handleFileSelect} className="sr-only" />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-slate-500">ZIP up to 50MB</p>
              </div>
            </div>
          </div>
        )}

        {loading && <p className="text-center text-indigo-400">{loading}</p>}
      </div>

      {/* Hide upload button in onboarding mode - wizard handles submission */}
      {!isOnboarding && (
        <div className="mt-6">
          <button
            onClick={handleUpload}
            disabled={
              !!loading ||
              (uploadMode === 'paired'
                ? !validationResult?.isValid
                : !zipFile)
            }
            className={buttonClasses}
          >
            {loading ? 'Uploading...' : 'Upload & Process File'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingDataUploader;